import type { InputRouter } from "../../ui/input/router.ts";
import { isWorldPress } from "../../ui/input/world-press.ts";
import type { EmotesOffer } from "../menu/state.ts";
import type { SettingsStore } from "../settings.ts";
import { bindEmoteKeys, bindEmoteScroll } from "./keys.ts";
import type { HudEmotes } from "./panel.tsx";
import { EmoteWheelStore } from "./store.ts";
import { targetHandle, type HeldTarget } from "./target.ts";

/**
 * The emote wheel on the game page (P2.19-T8; ADR-114 decisions 2.3, 2.4, 6.2, 6.7 and 6.8): its store, its keys and
 * mouse wheel, when the page refuses to open it and when an open wheel closes by itself.
 *
 * The wheel opens only for a character that is in the world, alive, with no death screen up, and not frozen. It does
 * not open on a frozen character's page because the server drops a frozen account's `emote` unanswered (the shared
 * freeze list, `odyssey-shared/src/gm/freeze.ts`), and a wheel that sent into silence would lie; the connection
 * withholds the message by that same list, so the page follows the server if the list changes. An open wheel closes
 * when its character dies, when a freeze arrives, and when the connection ends or the page leaves the world.
 */

/**
 * The selector of a control that keeps Enter for itself, the one the chat's Enter reads.
 */
const CONTROL = "button, a[href], [role='menuitem'], [role='option']";

/**
 * The selector of the wheel's own box.
 */
const WHEEL = "[data-emotes='wheel']";

/**
 * What the wheel reads of the HUD's store: the character's name, empty while no character is in the world, whether
 * it is dead, and the target held.
 */
interface HudReading {
  readonly name: string;
  readonly vitals: { readonly dead: boolean } | null;
  readonly target: HeldTarget | null;
}

/**
 * What the page gives the wheel.
 */
export interface EmotePageOptions {
  /** The page's input router. */
  readonly input: Pick<InputRouter, "open" | "onKey" | "onLayers" | "depth">;
  /** Where mouse wheel events, and the presses that say how the menu's item was chosen, are heard: the window. */
  readonly events: EventTarget;
  /** Where the last page and entry are kept, or null for no store. */
  readonly settings: SettingsStore | null;
  /** The HUD's store. */
  readonly hud: { readonly subscribe: (listener: () => void) => () => void; readonly getSnapshot: () => HudReading };
  /** The death screen, shown while its snapshot is not null. */
  readonly death: { readonly subscribe: (listener: () => void) => () => void; readonly getSnapshot: () => unknown };
  /** Whether a Game Master has frozen the player now. */
  readonly frozen: () => boolean;
  /** How many play windows are open, whose layers the wheel may open over. */
  readonly playWindows: () => number;
  /** The game's canvas, or null before the game is made. */
  readonly world: () => unknown;
}

/**
 * The wheel as the page holds it: its store, which is also the session's emote feed; what the HUD draws it from;
 * what the game menu's Emotes item needs; and `shut`, which closes an open wheel the page now refuses, for the page
 * to call when a freeze begins (`ModerationNotices.watchFreeze`, which tells of a freeze whatever notice shows).
 */
export interface EmotePage {
  readonly store: EmoteWheelStore;
  readonly hud: HudEmotes;
  readonly menu: { readonly offer: () => EmotesOffer; readonly open: () => void };
  readonly shut: () => void;
}

/**
 * Whether an event's target is a control that keeps Enter for itself.
 *
 * @param target What had focus.
 * @returns True for a button, a link, a menu item or an option, or anything inside one.
 */
function isControl(target: unknown): boolean {
  return typeof Element !== "undefined" && target instanceof Element && target.closest(CONTROL) !== null;
}

/**
 * Makes the wheel's store and binds it to the page.
 *
 * @param options The router, the window, the settings store, the HUD's store, the death screen, the freeze, the play
 *   windows open and the game's canvas.
 * @returns The wheel.
 * @example
 * const emotes = startEmoteWheel({ input, events: window, settings: localStore(), hud, death, frozen: () => false, playWindows: () => 0, world: () => game.canvas });
 * mountHud(host, hud, { emotes: emotes.hud });
 */
export function startEmoteWheel(options: EmotePageOptions): EmotePage {
  const { hud, death } = options;

  /**
   * Whether the page refuses to open the wheel now.
   *
   * @returns True with no character in the world, a dead one, the death screen up or a freeze standing.
   */
  const blocked = (): boolean => {
    const shown = hud.getSnapshot();
    return shown.name === "" || shown.vitals?.dead === true || death.getSnapshot() !== null || options.frozen();
  };
  const store = new EmoteWheelStore({ settings: options.settings, blocked, target: () => targetHandle(hud.getSnapshot().target) });

  /**
   * Closes an open wheel the page now refuses.
   */
  const shut = (): void => {
    if (store.getSnapshot().open && blocked()) {
      store.close();
    }
  };
  hud.subscribe(shut);
  death.subscribe(shut);
  const bound = bindEmoteKeys(store, options.input, { under: options.playWindows, isControl });

  /**
   * Whether the page's last press was a key's and not a pointer's, which says how the game menu's item was chosen: a
   * player with no mouse is put inside the wheel, on its selected entry, and a pointer's choice leaves focus on the
   * game. A held key's auto-repeat is not a press: a walking or running player who clicks the item keeps the pointer's
   * way in.
   */
  let byKey = false;
  options.events.addEventListener("keydown", (event) => {
    if (!(event as KeyboardEvent).repeat) {
      byKey = true;
    }
  });
  options.events.addEventListener("pointerdown", () => {
    byKey = false;
  });
  bindEmoteScroll(store, options.events, (target) => isWorldPress(target, options.world()) || (typeof Element !== "undefined" && target instanceof Element && target.closest(WHEEL) !== null));
  return {
    store,
    shut,
    hud: {
      source: store,
      keys: bound.live,
      takeEntry: () => store.takeEntry(),
      onTurn: (step) => store.turn(step),
      onGoTo: (page) => store.goTo(page),
      onSelect: (entry) => store.select(entry),
      onSend: () => void store.send(),
      onClose: () => store.close(),
    },
    menu: {
      offer: () => (!store.getSnapshot().offered ? "none" : blocked() ? "off" : "on"),
      open: () => void (byKey ? store.enter() : store.show()),
    },
  };
}
